import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, BroadcastMessage, BroadcastSendRequest, DebugConfig } from '@/lib/api'
import { Settings as SettingsIcon, Server, Key, Shield, Tv, Save, Check, Copy, ExternalLink, Info, MessageSquare, Send, Trash2, Clock, Wifi, Lock, Eye, EyeOff, Bug, AlertCircle, Clapperboard, Download, RefreshCw, Github, Package } from 'lucide-react'

interface SettingsData {
  emby_mode: boolean
  hls_segment_duration: number
  max_connections_per_user: number
  auto_restart_streams: boolean
  stream_idle_timeout: number
  default_user_agent: string
  site_name: string
  default_timezone: string
  epg_refresh_interval: number
  epg_retention_days: number
}

const defaultSettings: SettingsData = {
  emby_mode: false,
  hls_segment_duration: 2,
  max_connections_per_user: 1,
  auto_restart_streams: true,
  stream_idle_timeout: 60,
  default_user_agent: 'VLC/3.0.20 LibVLC/3.0.20',
  site_name: 'OBA Server',
  default_timezone: 'UTC',
  epg_refresh_interval: 6,
  epg_retention_days: 7,
}

export default function Settings() {
  const [activeTab, setActiveTab] = useState('streaming')
  const [settings, setSettings] = useState<SettingsData>(defaultSettings)
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null)
  const [copySuccess, setCopySuccess] = useState(false)
  const [versionPolicy, setVersionPolicy] = useState({
    recommended_phone: 226, recommended_tv: 226, recommended_auto: 226,
    minimum_phone: 225, minimum_tv: 225, minimum_auto: 225, reason: '',
  })

  // Broadcast state
  const [messageText, setMessageText] = useState('')
  const [messageType, setMessageType] = useState<'toast' | 'dialog'>('toast')
  const [messageTarget, setMessageTarget] = useState<'global' | 'stream'>('global')
  const [messageStreamID, setMessageStreamID] = useState('')
  const [messageSeverity, setMessageSeverity] = useState<'info' | 'warning' | 'critical'>('info')
  const [messageDuration, setMessageDuration] = useState(15)
  const [sendResult, setSendResult] = useState<string | null>(null)

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [passwordSuccess, setPasswordSuccess] = useState(false)

  // Debug API state
  const [debugConfig, setDebugConfig] = useState<DebugConfig>({
    enabled: false,
    max_log_lines: 100,
    allow_search: true,
    rate_limit: 60,
  })
  const [tokenCopied, setTokenCopied] = useState(false)

  // Cowboy Channel state
  const [cowboyEmail, setCowboyEmail] = useState('')
  const [cowboyPassword, setCowboyPassword] = useState('')
  const [cowboyEnabled, setCowboyEnabled] = useState(false)
  const [cowboySyncInterval, setCowboySyncInterval] = useState(30)
  const [showCowboyPassword, setShowCowboyPassword] = useState(false)
  const [cowboyTestResult, setCowboyTestResult] = useState<string | null>(null)
  const [cowboySyncResult, setCowboySyncResult] = useState<string | null>(null)
  const [cowboySyncing, setCowboySyncing] = useState(false)

  // APK Upload password state
  const [apkUploadPassword, setApkUploadPassword] = useState('')
  const [showApkUploadPassword, setShowApkUploadPassword] = useState(false)

  // APK Management state (GitHub sync)
  const [githubRepo, setGithubRepo] = useState('')
  const [githubToken, setGithubToken] = useState('')
  const [showGithubToken, setShowGithubToken] = useState(false)
  const [apkSyncing, setApkSyncing] = useState(false)
  const [apkSyncResult, setApkSyncResult] = useState<{ success: boolean; message: string } | null>(null)

  const queryClient = useQueryClient()

  // Fetch all settings
  const { data: settingsData } = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.getSettings(),
  })

  const { data: versionPolicyData } = useQuery({
    queryKey: ['client-version-policy'],
    queryFn: () => api.getClientVersionPolicy(),
  })

  useEffect(() => {
    if (versionPolicyData) setVersionPolicy(versionPolicyData)
  }, [versionPolicyData])

  const versionPolicyMutation = useMutation({
    mutationFn: () => api.updateClientVersionPolicy(versionPolicy),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-version-policy'] })
      setSaveSuccess('client-version-policy')
      setTimeout(() => setSaveSuccess(null), 2000)
    },
  })

  const rollbackVersionPolicyMutation = useMutation({
    mutationFn: () => api.rollbackClientVersionPolicy(`Rollback requested from Settings at ${new Date().toISOString()}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['client-version-policy'] }),
  })

  // Update local state when settings load
  useEffect(() => {
    if (settingsData) {
      setSettings({
        emby_mode: settingsData.emby_mode ?? defaultSettings.emby_mode,
        hls_segment_duration: settingsData.hls_segment_duration ?? defaultSettings.hls_segment_duration,
        max_connections_per_user: settingsData.max_connections_per_user ?? defaultSettings.max_connections_per_user,
        auto_restart_streams: settingsData.auto_restart_streams ?? defaultSettings.auto_restart_streams,
        stream_idle_timeout: settingsData.stream_idle_timeout ?? defaultSettings.stream_idle_timeout,
        default_user_agent: settingsData.default_user_agent ?? defaultSettings.default_user_agent,
        site_name: settingsData.site_name ?? defaultSettings.site_name,
        default_timezone: settingsData.default_timezone ?? defaultSettings.default_timezone,
        epg_refresh_interval: settingsData.epg_refresh_interval ?? defaultSettings.epg_refresh_interval,
        epg_retention_days: settingsData.epg_retention_days ?? defaultSettings.epg_retention_days,
      })
      // Load Cowboy Channel settings
      if (settingsData.cowboy_channel_email) setCowboyEmail(settingsData.cowboy_channel_email)
      if (settingsData.cowboy_channel_password) setCowboyPassword(settingsData.cowboy_channel_password)
      setCowboyEnabled(settingsData.cowboy_channel_enabled ?? false)
      setCowboySyncInterval(settingsData.cowboy_channel_sync_interval ?? 30)
      // Load APK upload password
      if (settingsData.apk_upload_password) setApkUploadPassword(settingsData.apk_upload_password)
      // Load GitHub APK settings
      if (settingsData.github_apk_repo) setGithubRepo(settingsData.github_apk_repo)
      if (settingsData.github_apk_token) setGithubToken(settingsData.github_apk_token)
    }
  }, [settingsData])

  // Fetch APK sync status
  const { data: apkSyncStatus, refetch: refetchApkStatus } = useQuery({
    queryKey: ['apk-sync-status'],
    queryFn: async () => {
      const token = localStorage.getItem('access_token')
      const resp = await fetch('/api/admin/downloads/sync-status', {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (!resp.ok) throw new Error('Failed to fetch APK status')
      return resp.json()
    },
  })

  // Save setting mutation
  const saveSettingMutation = useMutation({
    mutationFn: ({ key, value }: { key: string; value: unknown }) => api.setSetting(key, value),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['settings'] })
      setSaveSuccess(variables.key)
      setTimeout(() => setSaveSuccess(null), 2000)
    },
  })

  const saveSetting = (key: string, value: unknown) => {
    saveSettingMutation.mutate({ key, value })
  }

  const updateSetting = <K extends keyof SettingsData>(key: K, value: SettingsData[K]) => {
    setSettings(prev => ({ ...prev, [key]: value }))
  }

  // OBACast featured (hero) channel
  const { data: featuredData } = useQuery({
    queryKey: ['featured-channel'],
    queryFn: () => api.getFeaturedChannel(),
  })
  const { data: obacastStreamsData } = useQuery({
    queryKey: ['obacast-streams'],
    queryFn: () => api.getStreams({ per_page: 10000, is_active: 'true' }),
  })
  const setFeaturedMutation = useMutation({
    mutationFn: (id: string | null) => api.setFeaturedChannel(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['featured-channel'] })
      setSaveSuccess('featured_channel_id')
      setTimeout(() => setSaveSuccess(null), 2000)
    },
  })

  // Fetch broadcast history
  const { data: broadcastHistory, refetch: refetchHistory } = useQuery({
    queryKey: ['broadcast-history'],
    queryFn: () => api.getBroadcastHistory(),
  })

  // Send broadcast mutation
  const sendBroadcastMutation = useMutation({
    mutationFn: (message: BroadcastSendRequest) => api.sendBroadcast(message),
    onSuccess: (data) => {
      setSendResult(data.status === 'queued' ? 'Message queued for app overlay' : 'Message sent')
      setMessageText('')
      refetchHistory()
      setTimeout(() => setSendResult(null), 5000)
    },
    onError: (error: Error) => {
      setSendResult(`Failed to send: ${error.message}`)
      setTimeout(() => setSendResult(null), 5000)
    },
  })

  // Delete history item mutation
  const deleteHistoryMutation = useMutation({
    mutationFn: (id: string) => api.deleteBroadcastMessage(id),
    onSuccess: () => {
      refetchHistory()
    },
  })

  // Clear history mutation
  const clearHistoryMutation = useMutation({
    mutationFn: () => api.clearBroadcastHistory(),
    onSuccess: () => {
      refetchHistory()
    },
  })

  // Password change mutation
  const changePasswordMutation = useMutation({
    mutationFn: () => api.changePassword(currentPassword, newPassword),
    onSuccess: () => {
      setPasswordSuccess(true)
      setPasswordError(null)
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setTimeout(() => setPasswordSuccess(false), 5000)
    },
    onError: (error: any) => {
      setPasswordError(error.response?.data?.error || 'Failed to change password')
      setPasswordSuccess(false)
    },
  })

  // Fetch debug config
  const { data: debugConfigData, refetch: refetchDebugConfig } = useQuery({
    queryKey: ['debug-config'],
    queryFn: () => api.getDebugConfig(),
  })

  // Update local debug config state when data loads
  useEffect(() => {
    if (debugConfigData) {
      setDebugConfig(debugConfigData)
    }
  }, [debugConfigData])

  // Save debug config mutation
  const saveDebugConfigMutation = useMutation({
    mutationFn: (config: DebugConfig) => api.updateDebugConfig(config),
    onSuccess: () => {
      refetchDebugConfig()
      setSaveSuccess('debug_config')
      setTimeout(() => setSaveSuccess(null), 2000)
    },
  })

  const handlePasswordChange = () => {
    setPasswordError(null)

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('All fields are required')
      return
    }

    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters')
      return
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match')
      return
    }

    changePasswordMutation.mutate()
  }

  const handleEmbyModeToggle = () => {
    const newValue = !settings.emby_mode
    updateSetting('emby_mode', newValue)
    saveSetting('emby_mode', newValue)
    if (newValue && settings.hls_segment_duration === 2) {
      updateSetting('hls_segment_duration', 4)
      saveSetting('hls_segment_duration', 4)
    }
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    setCopySuccess(true)
    setTimeout(() => setCopySuccess(false), 2000)
  }

  const handleSendMessage = () => {
    if (!messageText.trim()) return

    const targetStreamID = messageTarget === 'stream' ? messageStreamID.trim() : undefined

    sendBroadcastMutation.mutate({
      text: messageText.trim(),
      timeout_ms: messageType === 'toast' ? messageDuration * 1000 : 0,
      target_stream_id: targetStreamID,
      severity: messageSeverity,
      placement: 'bottom_banner',
      display_type: messageType,
      duration_seconds: messageDuration,
    })
  }

  const handleResendMessage = (msg: BroadcastMessage) => {
    setMessageText(msg.text)
    setMessageType(msg.display_type === 'dialog' ? 'dialog' : 'toast')
    setMessageSeverity((msg.severity as 'info' | 'warning' | 'critical') || 'info')
    setMessageDuration(msg.duration_seconds || 15)
    if (msg.target_stream_id) {
      setMessageTarget('stream')
      setMessageStreamID(msg.target_stream_id)
    } else {
      setMessageTarget('global')
    }
  }

  // Generate example URLs
  const baseUrl = window.location.origin
  const exampleM3U = `${baseUrl}/playlist/username/password.m3u`
  const exampleXMLTV = `${baseUrl}/xmltv/username/password`

  const tabs = [
    { id: 'streaming', name: 'Streaming', icon: Tv },
    { id: 'broadcast', name: 'Broadcast', icon: MessageSquare },
    { id: 'general', name: 'General', icon: SettingsIcon },
    { id: 'obacast', name: 'OBACast App', icon: Tv },
    { id: 'security', name: 'Security', icon: Shield },
    { id: 'apk', name: 'APK Management', icon: Package },
    { id: 'api', name: 'API Keys', icon: Key },
    { id: 'cowboychannel', name: 'Cowboy Channel', icon: Clapperboard },
    { id: 'debug', name: 'Debug API', icon: Bug },
    { id: 'system', name: 'System', icon: Server },
  ]

  const copyToken = () => {
    const token = localStorage.getItem('access_token')
    if (token) {
      navigator.clipboard.writeText(token)
      setTokenCopied(true)
      setTimeout(() => setTokenCopied(false), 2000)
    }
  }

  const handleDebugToggle = () => {
    const newConfig = { ...debugConfig, enabled: !debugConfig.enabled }
    setDebugConfig(newConfig)
    saveDebugConfigMutation.mutate(newConfig)
  }

  const saveDebugConfig = () => {
    saveDebugConfigMutation.mutate(debugConfig)
  }

  const Toggle = ({ enabled, onChange, disabled }: { enabled: boolean; onChange: () => void; disabled?: boolean }) => (
    <button
      onClick={onChange}
      disabled={disabled}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
        enabled ? 'bg-primary' : 'bg-muted'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <span
        className={`${
          enabled ? 'translate-x-5' : 'translate-x-0'
        } inline-block h-5 w-5 transform rounded-full bg-white transition duration-200 ease-in-out`}
      />
    </button>
  )

  const SaveButton = ({ settingKey, onClick }: { settingKey: string; onClick: () => void }) => (
    <button
      onClick={onClick}
      disabled={saveSettingMutation.isPending}
      className="btn-primary whitespace-nowrap"
    >
      {saveSettingMutation.isPending ? (
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
      ) : saveSuccess === settingKey ? (
        <>
          <Check className="w-4 h-4" />
          Saved
        </>
      ) : (
        <>
          <Save className="w-4 h-4" />
          Save
        </>
      )}
    </button>
  )

  return (
    <div className="space-y-8">
      <h1 className="text-2xl sm:text-3xl font-semibold text-foreground">
        Settings
      </h1>

      <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
        {/* Tabs - horizontal scroll on mobile, vertical sidebar on lg */}
        <div className="lg:w-64 flex-shrink-0">
          <nav className="flex lg:flex-col gap-1 overflow-x-auto pb-2 lg:pb-0 lg:space-y-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 lg:gap-3 px-3 lg:px-4 py-2 lg:py-3 text-sm lg:text-base font-medium rounded-lg transition-colors whitespace-nowrap lg:w-full ${
                  activeTab === tab.id
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
                }`}
              >
                <tab.icon className="w-4 h-4 lg:w-5 lg:h-5" />
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 bg-card rounded-lg border border-border p-4 sm:p-6">
          {activeTab === 'streaming' && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-foreground">
                Streaming Settings
              </h2>

              {/* Emby Mode - Featured */}
              <div className="p-5 bg-gradient-to-r from-primary/10 to-primary/5 rounded-lg border border-primary/20">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-semibold text-foreground">Emby/Jellyfin Mode</h3>
                      <span className="px-2 py-0.5 text-xs font-medium bg-primary/20 text-primary rounded-full">Recommended</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      Optimizes HLS output, M3U playlists, and XMLTV EPG for perfect Emby/Jellyfin compatibility.
                    </p>
                    <ul className="mt-3 text-sm text-muted-foreground space-y-1">
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-500" />
                        4-second HLS segments (better buffering)
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-500" />
                        Emby-compatible M3U attributes
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-500" />
                        Optimized XMLTV format
                      </li>
                    </ul>
                  </div>
                  <Toggle enabled={settings.emby_mode} onChange={handleEmbyModeToggle} />
                </div>
              </div>

              {/* Emby Setup Guide */}
              {settings.emby_mode && (
                <div className="p-4 bg-muted/50 rounded-lg border border-border">
                  <div className="flex items-center gap-2 mb-3">
                    <Info className="w-5 h-5 text-primary" />
                    <h4 className="font-medium text-foreground">Quick Setup for Emby</h4>
                  </div>
                  <div className="space-y-3 text-sm">
                    <div>
                      <label className="text-muted-foreground">M3U Playlist URL:</label>
                      <div className="flex items-center gap-2 mt-1">
                        <code className="flex-1 bg-background px-3 py-2 rounded border text-xs overflow-x-auto">
                          {exampleM3U}
                        </code>
                        <button
                          onClick={() => copyToClipboard(exampleM3U)}
                          className="p-2 hover:bg-muted rounded"
                          title="Copy to clipboard"
                        >
                          {copySuccess ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="text-muted-foreground">XMLTV EPG URL:</label>
                      <div className="flex items-center gap-2 mt-1">
                        <code className="flex-1 bg-background px-3 py-2 rounded border text-xs overflow-x-auto">
                          {exampleXMLTV}
                        </code>
                        <button
                          onClick={() => copyToClipboard(exampleXMLTV)}
                          className="p-2 hover:bg-muted rounded"
                          title="Copy to clipboard"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <p className="text-muted-foreground flex items-center gap-1">
                      <ExternalLink className="w-4 h-4" />
                      In Emby: Settings → Live TV → Add Tuner Device → M3U Tuner
                    </p>
                  </div>
                </div>
              )}

              <div className="space-y-6">
                {/* User-Agent Setting */}
                <div className="p-4 bg-muted/50 rounded-lg border border-border">
                  <label className="form-label text-base font-medium">
                    Default User-Agent
                  </label>
                  <p className="text-sm text-muted-foreground mb-3">
                    The User-Agent header sent when fetching source streams. Some providers require a specific User-Agent.
                  </p>
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={settings.default_user_agent}
                      onChange={(e) => updateSetting('default_user_agent', e.target.value)}
                      className="input-field flex-1"
                      placeholder="VLC/3.0.20 LibVLC/3.0.20"
                    />
                    <SaveButton
                      settingKey="default_user_agent"
                      onClick={() => saveSetting('default_user_agent', settings.default_user_agent)}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Common examples: <code className="bg-muted px-1 rounded">VLC/3.0.20 LibVLC/3.0.20</code>,
                    <code className="bg-muted px-1 rounded ml-1">Lavf/60.3.100</code>,
                    <code className="bg-muted px-1 rounded ml-1">IPTV Smarters Pro</code>
                  </p>
                </div>

                {/* HLS Segment Duration */}
                <div>
                  <label className="form-label">
                    HLS Segment Duration (seconds)
                  </label>
                  <p className="text-sm text-muted-foreground mb-2">
                    Longer segments reduce load but increase latency. Emby works best with 4 seconds.
                  </p>
                  <div className="flex gap-3">
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={settings.hls_segment_duration}
                      onChange={(e) => updateSetting('hls_segment_duration', parseInt(e.target.value) || 2)}
                      className="input-field w-32"
                    />
                    <SaveButton
                      settingKey="hls_segment_duration"
                      onClick={() => saveSetting('hls_segment_duration', settings.hls_segment_duration)}
                    />
                  </div>
                </div>

                {/* Max Connections */}
                <div>
                  <label className="form-label">
                    Max Connections Per User
                  </label>
                  <p className="text-sm text-muted-foreground mb-2">
                    Maximum simultaneous streams a user can watch. Set to 0 for unlimited.
                  </p>
                  <div className="flex gap-3">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={settings.max_connections_per_user}
                      onChange={(e) => updateSetting('max_connections_per_user', parseInt(e.target.value) || 1)}
                      className="input-field w-32"
                    />
                    <SaveButton
                      settingKey="max_connections_per_user"
                      onClick={() => saveSetting('max_connections_per_user', settings.max_connections_per_user)}
                    />
                  </div>
                </div>

                {/* Stream Idle Timeout */}
                <div>
                  <label className="form-label">
                    Stream Idle Timeout (seconds)
                  </label>
                  <p className="text-sm text-muted-foreground mb-2">
                    How long to keep on-demand streams running without viewers before stopping.
                  </p>
                  <div className="flex gap-3">
                    <input
                      type="number"
                      min="30"
                      max="3600"
                      value={settings.stream_idle_timeout}
                      onChange={(e) => updateSetting('stream_idle_timeout', parseInt(e.target.value) || 60)}
                      className="input-field w-32"
                    />
                    <SaveButton
                      settingKey="stream_idle_timeout"
                      onClick={() => saveSetting('stream_idle_timeout', settings.stream_idle_timeout)}
                    />
                  </div>
                </div>

                {/* Auto-restart Toggle */}
                <div className="flex items-center justify-between py-2">
                  <div>
                    <p className="text-base font-medium text-foreground">
                      Auto-restart Failed Streams
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Automatically restart streams when the source fails or disconnects
                    </p>
                  </div>
                  <Toggle
                    enabled={settings.auto_restart_streams}
                    onChange={() => {
                      const newValue = !settings.auto_restart_streams
                      updateSetting('auto_restart_streams', newValue)
                      saveSetting('auto_restart_streams', newValue)
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'broadcast' && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-foreground">
                Broadcast Messages
              </h2>
              <p className="text-muted-foreground">
                Send native on-screen messages to OBACast Android and Android TV viewers during playback.
              </p>

              <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg text-sm text-blue-700 dark:text-blue-300">
                Messages are queued on the server and picked up by the OBACast Android app while a viewer is watching a stream. No Emby/Jellyfin client connection is required.
              </div>

              {/* Send Message */}
              <div className="p-5 bg-muted/50 rounded-lg border border-border space-y-4">
                <h3 className="text-lg font-medium text-foreground">Send Message</h3>

                <div>
                  <label className="form-label">Message</label>
                  <textarea
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    className="input-field w-full h-24 resize-none"
                    placeholder="System maintenance in 10 minutes. Please save your work."
                  />
                </div>

                {/* Target: Global vs Stream */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">Target</label>
                    <select
                      value={messageTarget}
                      onChange={(e) => setMessageTarget(e.target.value as 'global' | 'stream')}
                      className="input-field w-full"
                    >
                      <option value="global">All Viewers (Global)</option>
                      <option value="stream">Specific Stream</option>
                    </select>
                  </div>
                  {messageTarget === 'stream' && (
                    <div>
                      <label className="form-label">Stream ID</label>
                      <input
                        type="text"
                        value={messageStreamID}
                        onChange={(e) => setMessageStreamID(e.target.value)}
                        className="input-field w-full"
                        placeholder="Stream UUID (e.g., abc123-def456...)"
                      />
                    </div>
                  )}
                </div>

                {/* Duration and Severity */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="form-label">Duration (seconds)</label>
                    <input
                      type="number"
                      min="1"
                      max="300"
                      value={messageDuration}
                      onChange={(e) => setMessageDuration(parseInt(e.target.value) || 15)}
                      className="input-field w-full"
                    />
                  </div>
                  <div>
                    <label className="form-label">Severity</label>
                    <select
                      value={messageSeverity}
                      onChange={(e) => setMessageSeverity(e.target.value as 'info' | 'warning' | 'critical')}
                      className="input-field w-full"
                    >
                      <option value="info">Info</option>
                      <option value="warning">Warning</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>
                  <div>
                    <label className="form-label">Display Type</label>
                    <select
                      value={messageType}
                      onChange={(e) => setMessageType(e.target.value as 'toast' | 'dialog')}
                      className="input-field w-full"
                    >
                      <option value="toast">Toast (auto-dismiss)</option>
                      <option value="dialog">Dialog (requires OK)</option>
                    </select>
                  </div>
                </div>

                <button
                  onClick={handleSendMessage}
                  disabled={sendBroadcastMutation.isPending || !messageText.trim() || (messageTarget === 'stream' && !messageStreamID.trim())}
                  className="btn-primary"
                >
                  {sendBroadcastMutation.isPending ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : messageTarget === 'stream' ? (
                    <><Send className="w-4 h-4" /> Send to Stream</>
                  ) : (
                    <><Send className="w-4 h-4" /> Send to All</>
                  )}
                </button>

                {sendResult && (
                  <div className={`text-sm p-3 rounded-lg ${
                    sendResult.includes('Failed')
                      ? 'bg-destructive/10 text-destructive'
                      : 'bg-green-500/10 text-green-600 dark:text-green-400'
                  }`}>
                    {sendResult}
                  </div>
                )}
              </div>

              {/* Message History */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-foreground">Recent Messages</h3>
                  {broadcastHistory && broadcastHistory.length > 0 && (
                    <button
                      onClick={() => clearHistoryMutation.mutate()}
                      disabled={clearHistoryMutation.isPending}
                      className="btn-ghost text-sm text-muted-foreground hover:text-foreground"
                    >
                      Clear All
                    </button>
                  )}
                </div>

                {broadcastHistory && broadcastHistory.length > 0 ? (
                  <div className="space-y-2">
                    {broadcastHistory.map((msg) => (
                      <div
                        key={msg.id}
                        className="p-4 bg-muted/30 rounded-lg border border-border flex items-start justify-between gap-4"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-foreground">{msg.text}</p>
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {new Date(msg.sent_at).toLocaleString()}
                            </span>
                            {msg.duration_seconds !== undefined && (
                              <span>{msg.duration_seconds}s</span>
                            )}
                            {msg.severity !== undefined && msg.severity !== 'info' && (
                              <span className={`uppercase font-medium ${msg.severity === 'critical' ? 'text-red-500' : 'text-yellow-500'}`}>{msg.severity}</span>
                            )}
                            {msg.target_stream_id ? (
                              <span className="text-blue-500">Stream</span>
                            ) : (
                              <span>Global</span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          <button
                            onClick={() => handleResendMessage(msg)}
                            className="p-2 hover:bg-muted rounded text-muted-foreground hover:text-foreground"
                            title="Use this message"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => deleteHistoryMutation.mutate(msg.id)}
                            disabled={deleteHistoryMutation.isPending}
                            className="p-2 hover:bg-destructive/10 rounded text-muted-foreground hover:text-destructive"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    No messages sent yet
                  </p>
                )}
              </div>
            </div>
          )}

          {activeTab === 'general' && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-foreground">
                General Settings
              </h2>
              <div className="space-y-6">
                <div>
                  <label className="form-label">
                    Server Name
                  </label>
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={settings.site_name}
                      onChange={(e) => updateSetting('site_name', e.target.value)}
                      className="input-field flex-1"
                    />
                    <SaveButton
                      settingKey="site_name"
                      onClick={() => saveSetting('site_name', settings.site_name)}
                    />
                  </div>
                </div>

                <div>
                  <label className="form-label">
                    Timezone
                  </label>
                  <div className="flex gap-3">
                    <select
                      value={settings.default_timezone}
                      onChange={(e) => {
                        updateSetting('default_timezone', e.target.value)
                        saveSetting('default_timezone', e.target.value)
                      }}
                      className="input-field flex-1"
                    >
                      <option value="UTC">UTC</option>
                      <option value="America/New_York">America/New_York (Eastern)</option>
                      <option value="America/Chicago">America/Chicago (Central)</option>
                      <option value="America/Denver">America/Denver (Mountain)</option>
                      <option value="America/Los_Angeles">America/Los_Angeles (Pacific)</option>
                      <option value="Europe/London">Europe/London</option>
                      <option value="Europe/Paris">Europe/Paris</option>
                      <option value="Europe/Berlin">Europe/Berlin</option>
                      <option value="Asia/Tokyo">Asia/Tokyo</option>
                      <option value="Asia/Shanghai">Asia/Shanghai</option>
                      <option value="Australia/Sydney">Australia/Sydney</option>
                    </select>
                  </div>
                </div>

                {/* EPG Settings */}
                <div className="pt-4 border-t border-border">
                  <h3 className="text-lg font-medium text-foreground mb-4">EPG Settings</h3>

                  <div className="space-y-4">
                    <div>
                      <label className="form-label">
                        EPG Refresh Interval (hours)
                      </label>
                      <p className="text-sm text-muted-foreground mb-2">
                        How often to sync EPG data from sources
                      </p>
                      <div className="flex gap-3">
                        <input
                          type="number"
                          min="1"
                          max="24"
                          value={settings.epg_refresh_interval}
                          onChange={(e) => updateSetting('epg_refresh_interval', parseInt(e.target.value) || 6)}
                          className="input-field w-32"
                        />
                        <SaveButton
                          settingKey="epg_refresh_interval"
                          onClick={() => saveSetting('epg_refresh_interval', settings.epg_refresh_interval)}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="form-label">
                        EPG Retention (days)
                      </label>
                      <p className="text-sm text-muted-foreground mb-2">
                        How long to keep EPG program data
                      </p>
                      <div className="flex gap-3">
                        <input
                          type="number"
                          min="1"
                          max="14"
                          value={settings.epg_retention_days}
                          onChange={(e) => updateSetting('epg_retention_days', parseInt(e.target.value) || 7)}
                          className="input-field w-32"
                        />
                        <SaveButton
                          settingKey="epg_retention_days"
                          onClick={() => saveSetting('epg_retention_days', settings.epg_retention_days)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'security' && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-foreground">
                Security Settings
              </h2>

              <div className="p-5 bg-muted/50 rounded-lg border border-border space-y-4">
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-primary" />
                  <h3 className="text-lg font-medium text-foreground">Client Version Policy</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Recommended builds prompt without interrupting playback. Minimum builds block only new playback starts.
                </p>
                <div className="grid gap-3 md:grid-cols-3">
                  {(['phone', 'tv', 'auto'] as const).map((family) => (
                    <div key={family} className="rounded border border-border p-3 space-y-2">
                      <div className="font-medium capitalize">{family === 'auto' ? 'Android Auto' : family}</div>
                      <label className="text-xs text-muted-foreground">Recommended build</label>
                      <input type="number" min="0" className="input-field" value={versionPolicy[`recommended_${family}`]}
                        onChange={(e) => setVersionPolicy({ ...versionPolicy, [`recommended_${family}`]: Number(e.target.value) })} />
                      <label className="text-xs text-muted-foreground">Minimum build</label>
                      <input type="number" min="0" className="input-field" value={versionPolicy[`minimum_${family}`]}
                        onChange={(e) => setVersionPolicy({ ...versionPolicy, [`minimum_${family}`]: Number(e.target.value) })} />
                    </div>
                  ))}
                </div>
                <div>
                  <label className="form-label">Required operator reason</label>
                  <input className="input-field w-full" value={versionPolicy.reason}
                    onChange={(e) => setVersionPolicy({ ...versionPolicy, reason: e.target.value })}
                    placeholder="Known playback defect, release rollout, or rollback reason" />
                </div>
                <div className="flex gap-3">
                  <button className="btn-primary" disabled={!versionPolicy.reason.trim() || versionPolicyMutation.isPending}
                    onClick={() => versionPolicyMutation.mutate()}>
                    {saveSuccess === 'client-version-policy' ? 'Saved' : 'Apply Policy'}
                  </button>
                  <button className="btn-secondary" disabled={rollbackVersionPolicyMutation.isPending}
                    onClick={() => rollbackVersionPolicyMutation.mutate()}>
                    Roll Back Last Change
                  </button>
                </div>
              </div>

              {/* Change Admin Password */}
              <div className="p-5 bg-muted/50 rounded-lg border border-border space-y-4">
                <div className="flex items-center gap-2">
                  <Lock className="w-5 h-5 text-primary" />
                  <h3 className="text-lg font-medium text-foreground">Change Admin Password</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Update your admin account password. You'll need to log in again after changing.
                </p>

                {passwordError && (
                  <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-lg text-sm">
                    {passwordError}
                  </div>
                )}

                {passwordSuccess && (
                  <div className="bg-green-500/10 border border-green-500/20 text-green-600 dark:text-green-400 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
                    <Check className="w-4 h-4" />
                    Password changed successfully
                  </div>
                )}

                <div className="space-y-4">
                  <div>
                    <label className="form-label">Current Password</label>
                    <div className="relative">
                      <input
                        type={showCurrentPassword ? 'text' : 'password'}
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        className="input-field w-full pr-10"
                        placeholder="Enter current password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="form-label">New Password</label>
                    <div className="relative">
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="input-field w-full pr-10"
                        placeholder="Enter new password (min 8 characters)"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="form-label">Confirm New Password</label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="input-field w-full"
                      placeholder="Confirm new password"
                    />
                  </div>
                </div>

                <button
                  onClick={handlePasswordChange}
                  disabled={changePasswordMutation.isPending || !currentPassword || !newPassword || !confirmPassword}
                  className="btn-primary"
                >
                  {changePasswordMutation.isPending ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    <><Lock className="w-4 h-4" /> Change Password</>
                  )}
                </button>
              </div>

              {/* APK Upload Password */}
              <div className="p-5 bg-muted/50 rounded-lg border border-border space-y-4">
                <div className="flex items-center gap-2">
                  <Key className="w-5 h-5 text-primary" />
                  <h3 className="text-lg font-medium text-foreground">APK Upload Password</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Set a password to protect APK uploads on the /downloads page. This allows you to upload new Android TV app versions without SSH access.
                </p>

                <div>
                  <label className="form-label">Password</label>
                  <div className="flex gap-3">
                    <div className="relative flex-1">
                      <input
                        type={showApkUploadPassword ? 'text' : 'password'}
                        value={apkUploadPassword}
                        onChange={(e) => setApkUploadPassword(e.target.value)}
                        className="input-field w-full pr-10"
                        placeholder={apkUploadPassword ? '••••••••' : 'Not configured'}
                      />
                      <button
                        type="button"
                        onClick={() => setShowApkUploadPassword(!showApkUploadPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showApkUploadPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <SaveButton
                      settingKey="apk_upload_password"
                      onClick={() => saveSetting('apk_upload_password', apkUploadPassword)}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Leave empty to disable APK uploads. Use a strong password since this endpoint is publicly accessible.
                  </p>
                </div>
              </div>

              {/* Other Security Settings */}
              <div className="space-y-6 pt-4 border-t border-border">
                <div>
                  <label className="form-label">
                    JWT Expiration (hours)
                  </label>
                  <input
                    type="number"
                    defaultValue="24"
                    className="input-field w-32"
                    disabled
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Configured via environment variable
                  </p>
                </div>
                <div className="flex items-center justify-between py-2">
                  <div>
                    <p className="text-base font-medium text-foreground">
                      IP Restriction
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Restrict user access by IP address
                    </p>
                  </div>
                  <Toggle enabled={false} onChange={() => {}} />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'apk' && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-foreground">
                APK Management
              </h2>
              <p className="text-muted-foreground">
                Manage OBACast Android TV app releases. Sync from your private GitHub repository or upload manually.
              </p>

              {/* Current APK Status */}
              <div className="p-5 bg-muted/50 rounded-lg border border-border space-y-4">
                <div className="flex items-center gap-2">
                  <Download className="w-5 h-5 text-primary" />
                  <h3 className="text-lg font-medium text-foreground">Current Release</h3>
                </div>
                {apkSyncStatus?.current_version ? (
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Version:</span>
                      <span className="ml-2 text-foreground font-medium">{apkSyncStatus.current_version}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Size:</span>
                      <span className="ml-2 text-foreground">{apkSyncStatus.current_size}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Filename:</span>
                      <span className="ml-2 text-foreground">{apkSyncStatus.current_filename}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Updated:</span>
                      <span className="ml-2 text-foreground">{apkSyncStatus.current_updated}</span>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No APK currently available</p>
                )}
              </div>

              {/* GitHub Sync Configuration */}
              <div className="p-5 bg-gradient-to-r from-purple-500/10 to-purple-500/5 rounded-lg border border-purple-500/20 space-y-4">
                <div className="flex items-center gap-2">
                  <Github className="w-5 h-5 text-purple-400" />
                  <h3 className="text-lg font-medium text-foreground">GitHub Sync</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Automatically download APK releases from your private GitHub repository.
                </p>

                {/* GitHub Repo */}
                <div>
                  <label className="form-label">Repository (owner/repo)</label>
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={githubRepo}
                      onChange={(e) => setGithubRepo(e.target.value)}
                      className="input-field flex-1"
                      placeholder="username/obacast-android"
                    />
                    <SaveButton
                      settingKey="github_apk_repo"
                      onClick={() => saveSetting('github_apk_repo', githubRepo)}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Format: owner/repository (e.g., johndoe/my-android-app)
                  </p>
                </div>

                {/* GitHub Token */}
                <div>
                  <label className="form-label">Personal Access Token</label>
                  <div className="flex gap-3">
                    <div className="relative flex-1">
                      <input
                        type={showGithubToken ? 'text' : 'password'}
                        value={githubToken}
                        onChange={(e) => setGithubToken(e.target.value)}
                        className="input-field w-full pr-10"
                        placeholder={apkSyncStatus?.has_token ? '••••••••••••••••' : 'ghp_xxxxxxxxxxxx'}
                      />
                      <button
                        type="button"
                        onClick={() => setShowGithubToken(!showGithubToken)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showGithubToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <SaveButton
                      settingKey="github_apk_token"
                      onClick={() => saveSetting('github_apk_token', githubToken)}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Required for private repos. Create at GitHub → Settings → Developer settings → Personal access tokens.
                    Needs <code className="bg-muted px-1 rounded">repo</code> scope.
                  </p>
                </div>

                {/* Sync Button */}
                <div className="pt-2">
                  <button
                    onClick={async () => {
                      setApkSyncing(true)
                      setApkSyncResult(null)
                      try {
                        const token = localStorage.getItem('access_token')
                        const resp = await fetch('/api/admin/downloads/sync', {
                          method: 'POST',
                          headers: { 'Authorization': `Bearer ${token}` }
                        })
                        const data = await resp.json()
                        if (resp.ok && data.success) {
                          setApkSyncResult({
                            success: true,
                            message: `Synced ${data.filename} (${data.size_human}) from GitHub`
                          })
                          refetchApkStatus()
                        } else {
                          setApkSyncResult({
                            success: false,
                            message: data.error || 'Sync failed'
                          })
                        }
                      } catch (err) {
                        setApkSyncResult({
                          success: false,
                          message: 'Failed to connect to server'
                        })
                      } finally {
                        setApkSyncing(false)
                      }
                    }}
                    disabled={apkSyncing || !githubRepo}
                    className="btn-primary flex items-center gap-2"
                  >
                    {apkSyncing ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4" />
                    )}
                    {apkSyncing ? 'Syncing...' : 'Sync Latest Release'}
                  </button>
                </div>

                {/* Sync Result */}
                {apkSyncResult && (
                  <div className={`p-3 rounded-lg text-sm ${
                    apkSyncResult.success
                      ? 'bg-green-500/10 text-green-600 dark:text-green-400'
                      : 'bg-red-500/10 text-red-600 dark:text-red-400'
                  }`}>
                    {apkSyncResult.message}
                  </div>
                )}
              </div>

              {/* Install URL Info */}
              <div className="p-5 bg-muted/50 rounded-lg border border-border space-y-4">
                <div className="flex items-center gap-2">
                  <Info className="w-5 h-5 text-primary" />
                  <h3 className="text-lg font-medium text-foreground">Install URL</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Share this URL with users. Android devices will download the APK directly; others will see an install guide.
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-background px-3 py-2 rounded border text-sm overflow-x-auto">
                    {window.location.origin}/install
                  </code>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/install`)
                      setCopySuccess(true)
                      setTimeout(() => setCopySuccess(false), 2000)
                    }}
                    className="btn-secondary whitespace-nowrap"
                  >
                    {copySuccess ? (
                      <><Check className="w-4 h-4" /> Copied</>
                    ) : (
                      <><Copy className="w-4 h-4" /> Copy</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'api' && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-foreground">
                API Keys
              </h2>
              <p className="text-base text-muted-foreground">
                Manage API keys for external integrations
              </p>
              <button className="btn-primary">
                Generate New API Key
              </button>
            </div>
          )}

          {activeTab === 'debug' && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-foreground">
                Debug API
              </h2>
              <p className="text-muted-foreground">
                Access OBA's internal logs and diagnostics remotely for troubleshooting stream issues.
              </p>

              {/* JWT Token Display */}
              <div className="p-5 bg-muted/50 rounded-lg border border-border space-y-4">
                <div className="flex items-center gap-2">
                  <Key className="w-5 h-5 text-primary" />
                  <h3 className="text-lg font-medium text-foreground">Your JWT Token</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Use this token in the <code className="bg-muted px-1 rounded">Authorization: Bearer &lt;token&gt;</code> header when making API requests.
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-background px-3 py-2 rounded border text-xs overflow-x-auto font-mono">
                    {localStorage.getItem('access_token')?.substring(0, 50)}...
                  </code>
                  <button
                    onClick={copyToken}
                    className="btn-secondary whitespace-nowrap"
                  >
                    {tokenCopied ? (
                      <><Check className="w-4 h-4" /> Copied</>
                    ) : (
                      <><Copy className="w-4 h-4" /> Copy Token</>
                    )}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Token expires after 24 hours. Re-login to get a new token.
                </p>
              </div>

              {/* Enable/Disable Debug API */}
              <div className="p-5 bg-gradient-to-r from-orange-500/10 to-orange-500/5 rounded-lg border border-orange-500/20">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-semibold text-foreground">Enable Debug API</h3>
                      {debugConfig.enabled && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-green-500/20 text-green-600 dark:text-green-400 rounded-full">Active</span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      When enabled, authenticated users can access detailed stream logs and diagnostics.
                    </p>
                    {debugConfig.enabled && (
                      <div className="mt-3 p-3 bg-orange-500/10 rounded-lg">
                        <div className="flex items-start gap-2 text-sm text-orange-600 dark:text-orange-400">
                          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <span>For security, disable this when not actively debugging.</span>
                        </div>
                      </div>
                    )}
                  </div>
                  <Toggle enabled={debugConfig.enabled} onChange={handleDebugToggle} />
                </div>
              </div>

              {/* Debug API Configuration */}
              <div className="space-y-6 pt-4 border-t border-border">
                <h3 className="text-lg font-medium text-foreground">Configuration</h3>

                {/* Max Log Lines */}
                <div>
                  <label className="form-label">
                    Max Log Lines Per Stream
                  </label>
                  <p className="text-sm text-muted-foreground mb-2">
                    Maximum number of log lines returned per stream (keeps most recent)
                  </p>
                  <div className="flex gap-3">
                    <input
                      type="number"
                      min="10"
                      max="1000"
                      value={debugConfig.max_log_lines}
                      onChange={(e) => setDebugConfig({ ...debugConfig, max_log_lines: parseInt(e.target.value) || 100 })}
                      className="input-field w-32"
                    />
                    <SaveButton
                      settingKey="debug_config"
                      onClick={saveDebugConfig}
                    />
                  </div>
                </div>

                {/* Rate Limit */}
                <div>
                  <label className="form-label">
                    Rate Limit (requests/minute)
                  </label>
                  <p className="text-sm text-muted-foreground mb-2">
                    Maximum debug API requests per minute per IP. Set to 0 for unlimited.
                  </p>
                  <div className="flex gap-3">
                    <input
                      type="number"
                      min="0"
                      max="1000"
                      value={debugConfig.rate_limit}
                      onChange={(e) => setDebugConfig({ ...debugConfig, rate_limit: parseInt(e.target.value) || 0 })}
                      className="input-field w-32"
                    />
                    <SaveButton
                      settingKey="debug_config"
                      onClick={saveDebugConfig}
                    />
                  </div>
                </div>

                {/* Allow Search Toggle */}
                <div className="flex items-center justify-between py-2">
                  <div>
                    <p className="text-base font-medium text-foreground">
                      Allow Log Search
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Enable the <code className="bg-muted px-1 rounded">/api/debug/logs/search</code> endpoint
                    </p>
                  </div>
                  <Toggle
                    enabled={debugConfig.allow_search}
                    onChange={() => {
                      const newConfig = { ...debugConfig, allow_search: !debugConfig.allow_search }
                      setDebugConfig(newConfig)
                      saveDebugConfigMutation.mutate(newConfig)
                    }}
                  />
                </div>
              </div>

              {/* API Endpoints Reference */}
              <div className="p-5 bg-muted/50 rounded-lg border border-border space-y-4">
                <div className="flex items-center gap-2">
                  <Info className="w-5 h-5 text-primary" />
                  <h3 className="text-lg font-medium text-foreground">Available Endpoints</h3>
                </div>
                <div className="space-y-3 text-sm">
                  <div className="flex items-start gap-2">
                    <code className="bg-background px-2 py-1 rounded border text-xs whitespace-nowrap">GET</code>
                    <div>
                      <code className="text-muted-foreground">/api/debug/config</code>
                      <p className="text-xs text-muted-foreground mt-0.5">Get current debug API configuration</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <code className="bg-background px-2 py-1 rounded border text-xs whitespace-nowrap">GET</code>
                    <div>
                      <code className="text-muted-foreground">/api/debug/logs</code>
                      <p className="text-xs text-muted-foreground mt-0.5">List all active streams with logs</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <code className="bg-background px-2 py-1 rounded border text-xs whitespace-nowrap">GET</code>
                    <div>
                      <code className="text-muted-foreground">/api/debug/logs/search?q=error</code>
                      <p className="text-xs text-muted-foreground mt-0.5">Search through all stream logs</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <code className="bg-background px-2 py-1 rounded border text-xs whitespace-nowrap">GET</code>
                    <div>
                      <code className="text-muted-foreground">/api/debug/streams/:id</code>
                      <p className="text-xs text-muted-foreground mt-0.5">Get detailed diagnostics for a stream</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <code className="bg-background px-2 py-1 rounded border text-xs whitespace-nowrap">GET</code>
                    <div>
                      <code className="text-muted-foreground">/api/debug/health</code>
                      <p className="text-xs text-muted-foreground mt-0.5">Get overall stream health info</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <code className="bg-background px-2 py-1 rounded border text-xs whitespace-nowrap">GET</code>
                    <div>
                      <code className="text-muted-foreground">/api/debug/ffmpeg</code>
                      <p className="text-xs text-muted-foreground mt-0.5">List running FFmpeg processes</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'cowboychannel' && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-foreground">
                Cowboy Channel Plus
              </h2>
              <p className="text-sm text-muted-foreground">
                Configure Cowboy Channel Plus integration to automatically sync live rodeo streams and EPG data.
              </p>

              {/* Enable toggle */}
              <div className="flex items-center justify-between py-3 border-b border-border">
                <div>
                  <label className="text-base font-medium text-foreground">Enable Sync</label>
                  <p className="text-sm text-muted-foreground">Automatically sync live rodeo events</p>
                </div>
                <button
                  onClick={() => {
                    const newVal = !cowboyEnabled
                    setCowboyEnabled(newVal)
                    saveSetting('cowboy_channel_enabled', newVal)
                  }}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${cowboyEnabled ? 'bg-primary' : 'bg-gray-400'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${cowboyEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Email</label>
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={cowboyEmail}
                    onChange={(e) => setCowboyEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="flex-1 px-3 py-2 bg-secondary border border-border rounded-lg text-foreground"
                  />
                  <button
                    onClick={() => saveSetting('cowboy_channel_email', cowboyEmail)}
                    className="btn-primary shrink-0"
                  >
                    {saveSuccess === 'cowboy_channel_email' ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Password</label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <input
                      type={showCowboyPassword ? 'text' : 'password'}
                      value={cowboyPassword}
                      onChange={(e) => setCowboyPassword(e.target.value)}
                      placeholder="********"
                      className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-foreground pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCowboyPassword(!showCowboyPassword)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showCowboyPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <button
                    onClick={() => saveSetting('cowboy_channel_password', cowboyPassword)}
                    className="btn-primary shrink-0"
                  >
                    {saveSuccess === 'cowboy_channel_password' ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Sync Interval */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Sync Interval (minutes)</label>
                <div className="flex gap-2">
                  <select
                    value={cowboySyncInterval}
                    onChange={(e) => setCowboySyncInterval(Number(e.target.value))}
                    className="flex-1 px-3 py-2 bg-secondary border border-border rounded-lg text-foreground"
                  >
                    <option value={10}>Every 10 minutes</option>
                    <option value={15}>Every 15 minutes</option>
                    <option value={30}>Every 30 minutes</option>
                    <option value={60}>Every 60 minutes</option>
                  </select>
                  <button
                    onClick={() => saveSetting('cowboy_channel_sync_interval', cowboySyncInterval)}
                    className="btn-primary shrink-0"
                  >
                    {saveSuccess === 'cowboy_channel_sync_interval' ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <button
                  onClick={async () => {
                    setCowboyTestResult(null)
                    try {
                      const resp = await fetch('/api/cowboychannel/test', { method: 'POST', headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` } })
                      const data = await resp.json()
                      setCowboyTestResult(data.success ? 'Connected successfully!' : `Failed: ${data.error}`)
                    } catch (err) {
                      setCowboyTestResult('Connection test failed')
                    }
                  }}
                  className="btn-secondary flex items-center gap-2"
                >
                  <Wifi className="w-4 h-4" />
                  Test Connection
                </button>
                <button
                  onClick={async () => {
                    setCowboySyncing(true)
                    setCowboySyncResult(null)
                    try {
                      const resp = await fetch('/api/cowboychannel/sync', { method: 'POST', headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` } })
                      const data = await resp.json()
                      setCowboySyncResult(data.message || 'Sync completed')
                    } catch (err) {
                      setCowboySyncResult('Sync failed')
                    } finally {
                      setCowboySyncing(false)
                    }
                  }}
                  disabled={cowboySyncing}
                  className="btn-primary flex items-center gap-2"
                >
                  {cowboySyncing ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  {cowboySyncing ? 'Syncing...' : 'Sync Now'}
                </button>
              </div>

              {/* Status messages */}
              {cowboyTestResult && (
                <div className={`p-3 rounded-lg text-sm ${cowboyTestResult.includes('success') ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
                  {cowboyTestResult}
                </div>
              )}
              {cowboySyncResult && (
                <div className="p-3 rounded-lg text-sm bg-blue-500/10 text-blue-400">
                  {cowboySyncResult}
                </div>
              )}
            </div>
          )}

          {activeTab === 'obacast' && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-foreground">OBACast App</h2>
              <div className="space-y-2 max-w-xl">
                <label className="form-label flex items-center gap-2">
                  <Tv className="w-4 h-4" /> Featured (hero) channel
                </label>
                <p className="text-sm text-muted-foreground">
                  Pin one channel to the OBACast home-screen hero. Leave on "None / Auto" to let the
                  app choose automatically. Changes reach running TVs within ~60 seconds.
                </p>
                <select
                  className="input-field"
                  value={featuredData?.featured_channel_id || ''}
                  onChange={(e) => setFeaturedMutation.mutate(e.target.value || null)}
                  disabled={setFeaturedMutation.isPending}
                >
                  <option value="">None / Auto</option>
                  {((obacastStreamsData?.streams as Array<{ id: string; name: string; channel_number?: number }>) || [])
                    .slice()
                    .sort(
                      (a, b) =>
                        (a.channel_number || 0) - (b.channel_number || 0) ||
                        a.name.localeCompare(b.name)
                    )
                    .map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.channel_number ? `${s.channel_number} · ` : ''}
                        {s.name}
                      </option>
                    ))}
                </select>
                {saveSuccess === 'featured_channel_id' && (
                  <p className="text-sm text-green-500 flex items-center gap-1">
                    <Check className="w-4 h-4" /> Saved
                  </p>
                )}
              </div>
            </div>
          )}

          {activeTab === 'system' && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-foreground">
                System Information
              </h2>
              <dl className="space-y-4">
                <div className="flex justify-between py-2 border-b border-border">
                  <dt className="text-base text-muted-foreground">Version</dt>
                  <dd className="text-base text-foreground font-medium">1.0.0</dd>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <dt className="text-base text-muted-foreground">Go Version</dt>
                  <dd className="text-base text-foreground font-medium">1.22</dd>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <dt className="text-base text-muted-foreground">Database</dt>
                  <dd className="text-base text-foreground font-medium">PostgreSQL 16</dd>
                </div>
                <div className="flex justify-between py-2">
                  <dt className="text-base text-muted-foreground">Cache</dt>
                  <dd className="text-base text-foreground font-medium">Redis 7</dd>
                </div>
              </dl>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
